import { AssetId } from "@carnegie-learning/common-types";
import { Asset, assetTypeIds } from "@carnegie-learning/cure-types";
import { FeathersServicePagination, FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { Action, createReducer, on } from "@ngrx/store";
import {
    SelectedAssets,
} from "cli-types";
import * as assetsActions from "../actions/assets.actions";
import {
    INTENT_PICK,
    INTENT_STORAGE_KEY,
} from "#core/constants/intent-mode.constants";

export const defaultAssetsPaginationParams: FeathersServicePaginationParameters<Asset> = {
    limit: 10,
    skip: 0,
    filter: {},
    sorting: {"$sort[displayName]": 1},
};
export interface AssetsState {
    entities: Map<string, Asset>;
    loading: boolean;
    loaded: boolean;
    selectedAssets: SelectedAssets;
    selectedAsset: Asset | null;
    rawResponse: FeathersServicePagination<Asset> | null;
    paginationParams: FeathersServicePaginationParameters<Asset>;
    assetsTab: number;
    distinctFilterDefinitions: Map<string, string[]>;
    distinctPublishedByFilter: Map<string, string[]>;
    algoliaSearchResult: Asset[];
    clonedAsset: Asset | null;
    updatedBundle: Asset | null;
}

export const initialState: AssetsState = {
    entities: new Map<AssetId, Asset>(),
    loading: false,
    loaded: false,
    selectedAssets: { editionId: "", assets: [] },
    selectedAsset: null,
    rawResponse: null,
    paginationParams: defaultAssetsPaginationParams,
    assetsTab: 0,
    distinctFilterDefinitions: new Map<string, string[]>(),
    distinctPublishedByFilter: new Map<string, string[]>(),
    algoliaSearchResult: [],
    clonedAsset: null,
    updatedBundle: null,
};

const assetReducer = createReducer(
    initialState,
    on(assetsActions.LoadAssets, (state, { paginationParams }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAssetsSuccess, (state, { payload }) => {
        const assets: Asset[] = payload.data;
        const paginationTotal = payload.total;
        let paginationParams = { ...state.paginationParams };
        paginationParams = { ...paginationParams, total: paginationTotal };

        const entities = new Map<AssetId, Asset>();
        assets.forEach((asset) => {
            entities.set(asset.assetId, asset);
        });

        // then re-bind the entity const with our returned state
        return {
            ...state,
            loading: false,
            loaded: true,
            entities: entities,
            rawResponse: payload,
            paginationParams: paginationParams,
        };
    }),
    on(
        assetsActions.LoadAssetsFail,
        assetsActions.LoadAssetFail,
        assetsActions.createAssetFail,
        assetsActions.updateAssetFail,
        (state, { payload }) => {
            return {
                ...state,
                loading: false,
                loaded: false,
            };
        }
    ),
    on(assetsActions.LoadAssetsFromIds, (state, { assetIds }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAssetsFromIdsSuccess, (state, { payload }) => {
        const assets: FeathersServicePagination<Asset> = payload;

        const entities = new Map<string, Asset>();
        assets.data.forEach((asset) => {
            entities.set(asset.assetId, asset);
        });

        // then re-bind the entity const with our returned state
        return {
            ...state,
            loading: false,
            loaded: true,
            entities: entities,
        };
    }),
    on(assetsActions.LoadAssetsFromIdsFail, (state, { payload }) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAsset, (state, { assetId }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.LoadAssetSuccess, (state, { payload }) => {
        return {
            ...state,
            loading: false,
            loaded: true,
            selectedAsset: payload,
        };
    }),
    on(assetsActions.SetSelectedAssets, (state, { payload }) => {
        return {
            ...state,
            selectedAssets: enforceIntentPickSingleAsset(payload),
        };
    }),
    on(assetsActions.SetSelectedAsset, (state, { payload }) => {
        return {
            ...state,
            selectedAsset: payload,
        };
    }),
    on(assetsActions.SetAssetsPaginationParams, (state, { payload }) => {
        // Merge parameter values, don't overwrite like LOAD_RESOURCES does.
        const inputParams = payload;
        const paginationParams: FeathersServicePaginationParameters<Asset> = {
            ...state.paginationParams,
        };
        const filter = {};
        if (inputParams) {
            Object.keys(inputParams).forEach(function (key) {
                (<any>paginationParams)[key] = inputParams[key];
            });
        }

        return {
            ...state,
            paginationParams: paginationParams,
        };
    }),
    on(assetsActions.SetAssetsTab, (state, { payload }) => {
        return {
            ...state,
            assetsTab: payload,
        };
    }),
    on(assetsActions.LoadDistinctFiltersDefinitions, (state) => {
        const entities = new Map<string, string[]>();
        return {
            ...state,
            loading: true,
            loaded: false,
            distinctFilterDefinitions: entities,
        };
    }),
    on(assetsActions.LoadDistinctFilterDefinitionsFail, (state) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(
        assetsActions.LoadDistinctFilterDefinitionsSuccess,
        (state, { attributes, payload }) => {
            const entities = new Map<string, string[]>(
                state.distinctFilterDefinitions.entries()
            );
            const excludeTypeFromFilter: Array<string> = [
                assetTypeIds.COG_TUTOR,
                assetTypeIds.EXT_LINK,
            ];
            payload.forEach((each, i) => {
                // filtering COG-TUTOR for the response, it is an assetTypeId but need to use its assetActivityTypeId e.g.MASTERY
                // TODO: Sort by numeric if all values are numbers, or string...
                const values = each
                    .filter((e) => !excludeTypeFromFilter.includes(e))
                    .sort();
                const existingValues = entities.get(attributes[i]);
                if (existingValues?.length) {
                    existingValues.forEach((eValue) => {
                        if (!values.includes(eValue)) {
                            values.push(eValue);
                        }
                    });
                }
                entities.set(attributes[i], values);
            });

            return {
                ...state,
                loading: false,
                loaded: true,
                distinctFilterDefinitions: entities,
            };
        }
    ),
    on(
        assetsActions.LoadDistinctPublishedByFilterSuccess,
        (state, { keys, payload }) => {
            const entities = new Map<string, string[]>(
                state.distinctPublishedByFilter.entries()
            );
            payload.forEach((guids, i) => {
                entities.set(keys[i], guids);
            });

            return {
                ...state,
                loading: false,
                loaded: true,
                distinctPublishedByFilter: entities,
            };
        }
    ),
    on(
        assetsActions.createAssetSuccess,
        (state, { payload, createAdd, createAddBundle }) => {
            const entities = new Map<AssetId, Asset>(state.entities.entries());
            entities.set(payload.assetId, payload);
            let selectedAssets = state.selectedAssets;

            // If creating asset, it's a bundle, and we're going to the assign page, set that bundle asset as the only selectedAsset
            if (createAdd && createAddBundle && selectedAssets) {
                selectedAssets = { ...selectedAssets, assets: [payload] };
            } else if (createAdd && selectedAssets) {
                let sAssets = selectedAssets.assets;
                sAssets = [...sAssets, payload];
                selectedAssets = { ...selectedAssets, assets: sAssets };
            }

            selectedAssets = enforceIntentPickSingleAsset(selectedAssets);

            return {
                ...state,
                entities: entities,
                selectedAssets,
            };
        }
    ),
    on(assetsActions.updateAssetSuccess, (state, { payload }) => {
        const entities = new Map<AssetId, Asset>(state.entities.entries());
        entities.set(payload.assetId, payload);

        const searchResults = state.algoliaSearchResult.map((a) => {
            return a.assetId === payload.assetId ? { ...payload } : a;
        });

        return {
            ...state,
            entities: entities,
            algoliaSearchResult: searchResults,
            selectedAsset: payload,
            updatedBundle: payload,
        };
    }),
    on(assetsActions.removeAssetSuccess, (state, { payload }) => {
        const entities = new Map<AssetId, Asset>(state.entities.entries());
        entities.delete(payload.assetId);

        const searchResults = state.algoliaSearchResult.filter(
            (a) => a.assetId !== payload.assetId
        );

        let paginationParams = { ...state.paginationParams };
        if (state.paginationParams.total) {
            paginationParams = {
                ...paginationParams,
                total: state.paginationParams.total - 1,
            };
        }

        return {
            ...state,
            entities: entities,
            algoliaSearchResult: searchResults,
            paginationParams: paginationParams,
        };
    }),
    on(assetsActions.loadBundlesTasks, (state) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.loadBundlesTasksFail, (state) => {
        return {
            ...state,
            loading: false,
            loaded: false,
        };
    }),
    on(
        assetsActions.loadedAssetsFromAlgolia,
        assetsActions.loadBundlesTasksSuccess,
        (state, { payload }) => {
            const assets = payload;
            return {
                ...state,
                loading: false,
                loaded: true,
                algoliaSearchResult: assets,
            };
        }
    ),
    on(assetsActions.cloneAsset, (state) => {
        return {
            ...state,
            loading: true,
            loaded: false,
        };
    }),
    on(assetsActions.clearClonedAsset, (state) => {
        return {
            ...state,
            clonedAsset: null,
        };
    }),
    on(assetsActions.cloneAssetSuccess, (state, { payload }) => {
        const clonedAsset = payload;
        const entities = new Map<AssetId, Asset>(state.entities.entries());
        entities.set(payload.assetId, clonedAsset);

        return {
            ...state,
            loading: false,
            loaded: true,
            entities: entities,
            clonedAsset,
        };
    }),
    on(assetsActions.clearLesson, (state) => {
        return {
            ...state,
            teacherSelectedLesson: {},
        };
    }),
    on(assetsActions.clearUpdatedBundle, (state) => {
        return {
            ...state,
            updatedBundle: null,
        };
    })
);

// exporting the reducer so it can be added to the index file
export function reducer(state: AssetsState | undefined, action: Action) {
    return assetReducer(state, action);
}

function enforceIntentPickSingleAsset(selectedAssets: SelectedAssets): SelectedAssets {
    if (!isIntentPickMode() || !selectedAssets?.assets?.length) {
        return selectedAssets;
    }

    if (selectedAssets.assets.length <= 1) {
        return selectedAssets;
    }

    return {
        ...selectedAssets,
        assets: [selectedAssets.assets[0]],
    };
}

function isIntentPickMode(): boolean {
    if (globalThis.window === undefined) {
        return false;
    }

    const intent = globalThis.sessionStorage.getItem(INTENT_STORAGE_KEY);
    return intent === INTENT_PICK;
}

export const getAssetsEntities = (state: AssetsState) => state.entities;
export const getAssetsLoading = (state: AssetsState) => state.loading;
export const getAssetsLoaded = (state: AssetsState) => state.loaded;
export const getSelectedAssets = (state: AssetsState) => state.selectedAssets;
export const getRawAssetsResponse = (state: AssetsState) => state.rawResponse;
export const getAssetPaginationParams = (state: AssetsState) =>
    state.paginationParams;
export const getSelectedAsset = (state: AssetsState) => state.selectedAsset;
export const getSelectedAssetLoading = (state: AssetsState) => state.loading;
export const getSelectedAssetLoaded = (state: AssetsState) => state.loaded;
export const getAssetsTab = (state: AssetsState) => state.assetsTab;
export const getDistinctFilterDefinitions = (state: AssetsState) =>
    state.distinctFilterDefinitions;
export const getDistinctPublishedByFilter = (state: AssetsState) =>
    state.distinctPublishedByFilter;
export const getAlgoliaSearchResult = (state: AssetsState) =>
    state.algoliaSearchResult;
export const getClonedAsset = (state: AssetsState) => state.clonedAsset;
export const getUpdatedBundle = (state: AssetsState) => state.updatedBundle;
